import { useState, useMemo } from 'react';
import {
  Modal, Stack, TextInput, ScrollArea, Group, Text, Button, Checkbox, Badge, Box,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import fuzzysort from 'fuzzysort';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { useVideos } from '@/features/media/useVideos';
import type { MediaType } from '@/lib/types/Media';

interface VideoPickerModalProps {
  opened: boolean;
  onClose: () => void;
  onAdd: (mediaIds: number[]) => void;
  excludeMediaIds: number[];
  mediaType: MediaType;
}

const MAX_RESULTS = 50;

export function VideoPickerModal({
  opened,
  onClose,
  onAdd,
  excludeMediaIds,
  mediaType,
}: VideoPickerModalProps) {
  const { videos } = useVideos();
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const excludeSet = useMemo(() => new Set(excludeMediaIds), [excludeMediaIds]);

  // Filter by mediaType and exclude already-in-playlist
  const candidates = useMemo(() => {
    return videos.filter(v =>
      v.media.mediaType === mediaType && !excludeSet.has(v.media.mediaId)
    );
  }, [videos, mediaType, excludeSet]);

  // Prepare search targets
  const prepared = useMemo(() => {
    return candidates.map(v => ({
      video: v,
      prepared: fuzzysort.prepare(v.media.title),
    }));
  }, [candidates]);

  // Apply search filter
  const { results, totalMatching } = useMemo(() => {
    if (!searchText.trim()) {
      const sliced = candidates.slice(0, MAX_RESULTS);
      return { results: sliced, totalMatching: candidates.length };
    }
    const matches = fuzzysort.go(searchText, prepared, {
      key: 'prepared',
      limit: MAX_RESULTS + 100,
    });
    return {
      results: matches.slice(0, MAX_RESULTS).map(m => m.obj.video),
      totalMatching: matches.length,
    };
  }, [searchText, candidates, prepared]);

  function toggleVideo(mediaId: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  }

  function handleAdd() {
    onAdd(Array.from(selectedIds));
    handleClose();
  }

  function handleClose() {
    setSearchText('');
    setSelectedIds(new Set());
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Videos"
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search videos..."
          leftSection={<IconSearch size={16} />}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
        />

        {totalMatching > MAX_RESULTS && (
          <Text size="xs" c="dimmed">
            Showing first {MAX_RESULTS} of {totalMatching} results. Refine your search to see more.
          </Text>
        )}

        <ScrollArea h={400}>
          {results.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No matching videos found. Try a different search.
            </Text>
          ) : (
            <Stack gap={0}>
              {results.map(video => {
                const mediaId = video.media.mediaId;
                const isSelected = selectedIds.has(mediaId);
                const isActive = video.media.mediaStateType === 'active';
                return (
                  <Box
                    key={mediaId}
                    px="sm"
                    py="xs"
                    onClick={() => toggleVideo(mediaId)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                      backgroundColor: isSelected ? 'var(--mantine-primary-color-light)' : undefined,
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleVideo(mediaId)}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" truncate="end">{video.media.title}</Text>
                        <Text size="xs" c="dimmed">{bcLong.getStr(mediaId)}</Text>
                      </div>
                      <Badge
                        size="xs"
                        variant="light"
                        color={isActive ? 'green' : 'gray'}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Group>
                  </Box>
                );
              })}
            </Stack>
          )}
        </ScrollArea>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {selectedIds.size} selected
          </Text>
          <Group gap="sm">
            <Button variant="default" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
              Add Selected
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
